import { useLayoutEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import { selectHoveredCity } from "src/store/ui"
import {
  selectCityMetadataByUser,
  selectGame,
  selectGamePlayers,
  selectMovements,
  selectViewerId,
} from "src/store/game"
import { cellDisplayName, forceDisclosure, getPlace, isWater, movementsTouching } from "src/utils/maps"
import { crownCells } from "src/utils/crowns"

import "./CellTooltip.css"

const unitsIn = (metadata) =>
  Object.values(metadata?.units || {}).reduce((total, unit) => total + (unit.number || 0), 0)

/* Where the box goes, given the pointer and the box's own size (owner's S6, 2026-09-05): beside
   the pointer, below and to the right; flipped to the other side of the pointer where that
   would run off the screen; and clamped inside the viewport either way, so a box wider than
   the room on both sides is still whole. It used to flip on a guessed width, so a tooltip
   with three movement lines on it still ran under the right dock. Pure, so the test can pin
   every edge. */
export const placeTooltip = ({
  clientX,
  clientY,
  width,
  height,
  viewportWidth,
  viewportHeight,
  margin = 14,
  gutter = 8,
}) => {
  const clamp = (value, max) => Math.max(gutter, Math.min(value, max - gutter))
  let left = clientX + margin
  if (left + width > viewportWidth - gutter) left = clientX - margin - width
  let top = clientY + margin
  if (top + height > viewportHeight - gutter) top = clientY - margin - height
  return {
    left: clamp(left, viewportWidth - width),
    top: clamp(top, viewportHeight - height),
  }
}

/* What the pointer is resting on: name, coordinates, who holds it, your troops there. Read-only,
   beside the pointer, gone the moment the pointer moves on. Orders are given by *selecting* a cell
   (the action bar), never from here — one place to look, one place to act.

   Rendered into `document.body` rather than into the map: a fixed box inside a panel is still a
   child of that panel's stacking context, and the right dock sat over it. On the body there is
   no neighbour to sit under, and `--z-tooltip` puts it over every layer the game has. */
const CellTooltip = () => {
  const { t } = useTranslation()
  const cell = useSelector(selectHoveredCity)
  const userId = useSelector(selectViewerId)
  const players = useSelector(selectGamePlayers)
  const metadata = useSelector((state) => cell && selectCityMetadataByUser(state, cell))
  const movements = useSelector(selectMovements)
  const game = useSelector(selectGame)
  const boxRef = useRef(null)

  // Placed after layout, from the box's real size, before the frame paints: no flicker, and no
  // guessed width. Runs on every render — the box changes size as the pointer moves.
  useLayoutEffect(() => {
    const box = boxRef.current
    if (!box || !cell) return
    const { width, height } = box.getBoundingClientRect()
    const { left, top } = placeTooltip({
      clientX: cell.clientX,
      clientY: cell.clientY,
      width,
      height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    })
    box.style.left = `${left}px`
    box.style.top = `${top}px`
    box.style.visibility = "visible"
  })

  if (!cell) return null

  // Stacks leaving, heading for, or standing on this cell — so nobody has to click to learn it.
  const teamOf = (id) => game?.metadata?.users?.[String(id)]?.team
  const touching = movementsTouching(movements, cell, { userId, teamOf })
  const MAX_LINES = 3
  const unitsText = (units) =>
    units.map((u) => `${u.number} ${t(`city.units.${u.type}`, { defaultValue: u.type })}`).join(" · ")

  // The sea hovers and selects like any cell since 2026-09-08 (a march pauses on it while it
  // saves the points to cross); it is named as the sea and is nobody's to hold.
  const sea = isWater({ column: cell.col, row: cell.row })
  const { flag } = getPlace({ row: cell.row, column: cell.col })
  const name = sea ? cellDisplayName({ row: cell.row, column: cell.col }) : getPlace({ row: cell.row, column: cell.col }).name
  const ownerId = metadata?.owner ?? null
  const owner = players.find((p) => p.user.id === ownerId)
  const held =
    ownerId == null
      ? t(sea ? "game.tooltip.openSea" : "game.tooltip.unclaimed")
      : ownerId === userId
      ? t("game.tooltip.yours")
      : t("game.tooltip.heldBy", { username: owner?.user.username ?? "?" })
  const troops = unitsIn(metadata?.metadata_by_user?.[String(userId)])

  // A crown city says so, and says whose game it decides: this is where a player learns which
  // cities are worth attacking or defending, so the line spells the rule out rather than assuming
  // the badge on the map explained itself.
  const crown = crownCells(game?.metadata, players)[`${cell.col}-${cell.row}`]
  const crownPlayer = crown && players.find((p) => String(p.user.id) === crown.userId)
  const crownText = !crown
    ? null
    : String(userId) === crown.userId
    ? crown.held
      ? t("game.crowns.tooltipYours")
      : t("game.crowns.tooltipYoursLost")
    : crown.held
    ? t("game.crowns.tooltipTheirs", { username: crownPlayer?.user.username ?? "?" })
    : t("game.crowns.tooltipTheirsLost", { username: crownPlayer?.user.username ?? "?" })

  // The rounds a stack still has to walk. A held-up stack keeps its number beside the hold-up
  // (owner's S14, 2026-09-05): the fight is what it is waiting on, and the march walks on the
  // round the fight resolves, so the count is what is left once it does.
  const etaText = (m) =>
    m.halted
      ? `${t("game.movements.heldUp")}${
          m.roundsLeft > 0 ? ` · ${t("game.tooltip.move.afterBattle", { count: m.roundsLeft })}` : ""
        }`
      : m.roundsLeft > 0
      ? t("game.tooltip.move.eta", { count: m.roundsLeft })
      : ""

  // Hidden until the layout effect has measured and placed it: the first paint of a box at
  // (0, 0) is a flash in the wrong corner.
  const initial = { left: cell.clientX, top: cell.clientY, visibility: "hidden" }

  return createPortal(
    <div className="cell-tooltip" style={initial} role="tooltip" ref={boxRef}>
      {flag && <img src={flag} alt="" className="cell-tooltip-flag" />}
      <div>
        <p className="cell-tooltip-name">
          {name} <span className="cell-tooltip-coords">{cell.col}-{cell.row}</span>
        </p>
        <p className={`cell-tooltip-meta${ownerId === userId ? " mine" : ""}`}>
          {held}
          {troops > 0 ? ` · ${t("game.tooltip.troops", { count: troops })}` : ""}
        </p>
        {crownText && <p className={`cell-tooltip-crown${crown.held ? "" : " lost"}`}>👑 {crownText}</p>}
        {touching.slice(0, MAX_LINES).map((m) => {
          const eta = etaText(m)
          return (
            <p key={m.id} className={`cell-tooltip-move ${m.relation}`}>
              <span className="cell-tooltip-move-kind">
                ⚔{" "}
                {t(`game.tooltip.move.${m.roles[0]}`, {
                  who: t(`game.tooltip.move.${m.relation}`),
                })}
              </span>{" "}
              {m.origin.col}-{m.origin.row} → {m.destination.col}-{m.destination.row}
              {eta ? ` · ${eta}` : ""}
              {forceDisclosure(m) === "units"
                ? ` · ${unitsText(m.units)}`
                : forceDisclosure(m) === "unknown"
                ? ` · ${t("game.tooltip.move.unknown")}`
                : ""}
            </p>
          )
        })}
        {touching.length > MAX_LINES && (
          <p className="cell-tooltip-move">{t("game.tooltip.move.more", { count: touching.length - MAX_LINES })}</p>
        )}
      </div>
    </div>,
    document.body
  )
}

export default CellTooltip
